import {
  type FRAME_COLORS,
  MATERIAL_TYPES,
  type PricingProduct,
  type TILE_SIZES,
} from '@mixtiles/web-backend-shared'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import uniqBy from 'lodash/uniqBy'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import type {
  CartItem,
  PhotoBookCartItem,
  TilesCartItem,
} from 'stores/cartStore/CartItem'
import { isSinglePriceSingleSize } from 'components/Pricing/PricingUtil'
import { isFramedMaterial } from 'utils/materialTypeUtils'
import { getDiscountForSize } from 'components/Pricing/Discounts/Discounts'
import {
  type BulkMaterial,
  MIXED_COLORS,
  MIXED_MATERIALS,
  MIXED_SIZES,
} from 'pages/PhotoStyler/TileDesignerConsts'
import {
  type Discount,
  type DiscountsAPI,
  type DiscountTileColor,
  type DiscountTileSize,
  DiscountType,
} from 'components/Pricing/types/discount.types'
import type { PricingItem } from 'components/Pricing/types/pricingItem.types'

const MOST_EXPENSIVE_MATERIAL = MATERIAL_TYPES.WIDE_FRAME

export function getCartDiscount({
  cartItems,
  discounts,
  currency,
  pricingItems,
}: {
  cartItems: CartItem[]
  discounts: DiscountsAPI
  currency: string
  pricingItems: Record<string, PricingItem>
}): Discount | null {
  if (isAllPhotoBooks(cartItems)) {
    return getPhotoBooksOnlyDiscount({ cartItems, discounts, currency })
  }

  if (!isAllTiles(cartItems)) {
    return getDiscountForSize({
      discounts,
      tileSize: MIXED_SIZES,
      currency,
      materialType: MATERIAL_TYPES.CLASSIC,
    })
  }

  return getTilesOnlyDiscount({ cartItems, discounts, currency, pricingItems })
}

export function getCartDiscountProductType(
  cartItems: CartItem[]
): PRODUCT_TYPES {
  return isAllPhotoBooks(cartItems)
    ? PRODUCT_TYPES.PHOTO_BOOK
    : PRODUCT_TYPES.CLASSIC
}

function getPhotoBooksOnlyDiscount({
  cartItems,
  discounts,
  currency,
}: {
  cartItems: PhotoBookCartItem[]
  discounts: DiscountsAPI
  currency: string
}): Discount | null {
  const bookSizes = [...new Set(cartItems.map((item) => item.metadata.size))]
  const bookDiscounts = bookSizes.map((bookSize) =>
    getDiscountForSize({
      discounts,
      tileSize: bookSize as unknown as TILE_SIZES,
      currency,
      materialType: MATERIAL_TYPES.CLASSIC,
    })
  )

  // A MIX coupon can set the same discount on every book SKU, so differing sizes don't make the
  // cart mixed - it keeps the book discount rather than the mixed-sizes one.
  const [firstBookDiscount] = bookDiscounts
  if (
    firstBookDiscount &&
    bookDiscounts.every((discount) => hasSameTerms(discount, firstBookDiscount))
  ) {
    return firstBookDiscount
  }

  // Books have no mixed-sizes discount of their own, and the backend rejects a tile discountId on
  // a book order, so it resolves the mixed-sizes fallback itself when we send none.
  return null
}

// Each size carries its own discountId, so only the terms decide whether the books share a discount.
function hasSameTerms(discount: Discount | null, other: Discount): boolean {
  return (
    !!discount &&
    isEqual(
      omit(discount.metadata, ['discountId']),
      omit(other.metadata, ['discountId'])
    )
  )
}

function getTilesOnlyDiscount({
  cartItems,
  discounts,
  currency,
  pricingItems,
}: {
  cartItems: CartItem[]
  discounts: DiscountsAPI
  currency: string
  pricingItems: Record<string, PricingItem>
}): Discount | null {
  const tilePricingProducts = getTilePricingProducts(cartItems)

  const actualMaterialsInUse = uniqBy(
    tilePricingProducts.map((product) => ({
      material: product.materialType ?? MATERIAL_TYPES.CLASSIC,
      frameColor: product.frameColor,
    })),
    ({ material, frameColor }) => `${material}-${frameColor}`
  )

  const { bulkSize, bulkMaterial, bulkFrameColor } =
    getBulkProperties(tilePricingProducts)

  const isTilesShareSizeAndPrice = isSinglePriceSingleSize({
    tileSize: bulkSize,
    actualMaterialsInUse,
    pricingItems,
  })

  const discountMaterial = getDiscountMaterial(
    bulkMaterial,
    actualMaterialsInUse,
    isTilesShareSizeAndPrice
  )

  const discountFrameColor = getDiscountFrameColor(
    discountMaterial,
    bulkFrameColor,
    actualMaterialsInUse,
    isTilesShareSizeAndPrice
  )

  const discount = getDiscountForSize({
    discounts,
    tileSize: isTilesShareSizeAndPrice ? bulkSize : MIXED_SIZES,
    currency,
    materialType: discountMaterial,
    frameColor: discountFrameColor,
  })

  // The cart can't communicate a BUYXGETY discount (the designer can), so treat a
  // BUYXGETY match as no discount — sending its id would make the backend cut the
  // price with no explanation in the cart.
  return discount?.type === DiscountType.BUYXGETY ? null : discount
}

function isAllTiles(cartItems: CartItem[]): boolean {
  return cartItems.every((item) => item.productType === PRODUCT_TYPES.CLASSIC)
}

function isAllPhotoBooks(
  cartItems: CartItem[]
): cartItems is PhotoBookCartItem[] {
  return (
    cartItems.length > 0 &&
    cartItems.every((item) => item.productType === PRODUCT_TYPES.PHOTO_BOOK)
  )
}

function getTilePricingProducts(cartItems: CartItem[]): PricingProduct[] {
  return cartItems
    .filter(
      (item): item is TilesCartItem =>
        item.productType === PRODUCT_TYPES.CLASSIC
    )
    .flatMap((item) =>
      item.metadata.pricingProducts.map(({ pricingProduct }) => pricingProduct)
    )
}

function getDiscountMaterial(
  bulkMaterial: BulkMaterial,
  actualMaterialsInUse: { material: MATERIAL_TYPES }[],
  isTilesShareSizeAndPrice: boolean
): MATERIAL_TYPES {
  if (bulkMaterial !== MIXED_MATERIALS) return bulkMaterial

  if (isTilesShareSizeAndPrice) return actualMaterialsInUse[0].material

  return MOST_EXPENSIVE_MATERIAL
}

function getDiscountFrameColor(
  discountMaterial: MATERIAL_TYPES,
  bulkFrameColor: DiscountTileColor | undefined,
  actualMaterialsInUse: { frameColor?: FRAME_COLORS }[],
  isTilesShareSizeAndPrice: boolean
): DiscountTileColor | undefined {
  if (!isFramedMaterial(discountMaterial)) return undefined

  if (isTilesShareSizeAndPrice && bulkFrameColor === MIXED_COLORS) {
    return actualMaterialsInUse[0]?.frameColor
  }

  return bulkFrameColor
}

function getSharedOrMixedProperty<Value, Mixed>(
  values: Value[],
  mixed: Mixed
): Value | Mixed {
  const unique = new Set(values)
  return unique.size === 1 ? [...unique][0] : mixed
}

function getBulkProperties(products: PricingProduct[]): {
  bulkSize: DiscountTileSize
  bulkMaterial: BulkMaterial
  bulkFrameColor: DiscountTileColor | undefined
} {
  return {
    bulkSize: getSharedOrMixedProperty(
      products.map((product) => product.tileSize),
      MIXED_SIZES
    ),
    bulkMaterial: getSharedOrMixedProperty(
      products.map((product) => product.materialType ?? MATERIAL_TYPES.CLASSIC),
      MIXED_MATERIALS
    ),
    bulkFrameColor: getSharedOrMixedProperty(
      products.map((product) => product.frameColor),
      MIXED_COLORS
    ),
  }
}
